import type { BlockSaleForm } from "../booking/paymentWorkflow.js";
import { schoolLessonWriteupHref } from "./schoolLessonIdentity.js";

export type DiaryQuickActionGroup = "teaching" | "booking" | "admin";
export type DiaryQuickActionSurface = "status" | "primary" | "quick" | "more" | "danger";

export type DiaryQuickActionId =
  | "change-lesson"
  | "record-payment"
  | "write-note"
  | "assign-practice"
  | "view-student"
  | "attribute-booking"
  | "add-credits"
  | "mark-no-show"
  | "undo-no-show"
  | "mark-unpaid"
  | "cancel-lesson";

export interface DiaryQuickAction {
  id: string;
  action: DiaryQuickActionId;
  label: string;
  group: DiaryQuickActionGroup;
  priority: number;
  href?: string;
  /** Exact participant for an inline student match, including group lessons. */
  bookingId?: string;
  enabled: boolean;
  disabledReason?: string;
  surface: DiaryQuickActionSurface;
  emphasis: "primary" | "secondary" | "tertiary" | "danger";
}

export interface DiaryQuickActionEntry {
  bookingId: string;
  studentId: string | null;
  studentName: string | null;
  lessonId: string | null;
  isSchools: boolean;
}

export interface DiaryQuickActionContext {
  entries: readonly DiaryQuickActionEntry[];
  participantCount: number;
  showNotes: boolean;
  canViewStudent: boolean;
  canMutate: boolean;
  canTakePayment: boolean;
  /** Keep the payment entry visible, but disabled, for an already funded lesson. */
  paymentDisabledReason?: string;
  canRecordCancelledPayment?: boolean;
  canSetPracticeTodo: boolean;
  /** The one block-sale form this account may open here, from blockSaleForm. */
  blockSale: BlockSaleForm | null;
  noShowEligible: boolean;
  noShow: boolean;
  canReversePayment: boolean;
  canMatchStudent: boolean;
  cancelled: boolean;
}

/**
 * Describe the actions an appointment surface may offer without performing any
 * permission or business mutation itself.
 *
 * The inputs are facts already established by the host: server-scoped student
 * identity, existing capability checks, live payment classification and the
 * appointment state. The registry only turns those facts into a consistent
 * presentation model. Every linked route and mutation still enforces its own
 * authority.
 */
export function diaryQuickActions(context: DiaryQuickActionContext): DiaryQuickAction[] {
  const actions: DiaryQuickAction[] = [];
  const identified = context.entries.filter((entry) => entry.studentId !== null);
  const singleStudent = context.participantCount === 1 ? identified[0] ?? null : null;
  // Canonical School rows deliberately have no private Student FK. The explicit
  // domain flag is the authority here; booking-id formatting is only used to
  // navigate to the School write-up record, never to decide whether a pupil is
  // School data or an unmatched private booking.
  const schoolEntry = context.entries.find((entry) => entry.isSchools) ?? null;
  const isSchoolSession = schoolEntry !== null;

  if ((context.canTakePayment || context.paymentDisabledReason !== undefined) &&
    (!context.cancelled || context.canRecordCancelledPayment === true)) {
    actions.push({
      id: "record-payment",
      action: "record-payment",
      label: context.cancelled ? "Record payment received" : "Mark paid / apply credit",
      group: "admin",
      priority: 10,
      enabled: context.canTakePayment,
      disabledReason: context.canTakePayment ? undefined : context.paymentDisabledReason,
      surface: "status",
      emphasis: "primary",
    });
  }

  if (!context.cancelled && context.canMutate) {
    actions.push({
      id: "change-lesson",
      action: "change-lesson",
      label: "Reschedule",
      group: "booking",
      priority: 20,
      enabled: true,
      surface: "primary",
      emphasis: context.canTakePayment ? "secondary" : "primary",
    });
  }

  if (context.showNotes) {
    if (schoolEntry) {
      const href = schoolLessonWriteupHref(schoolEntry.bookingId);
      if (href) {
        actions.push({
          id: `write-school-note:${schoolEntry.bookingId}`,
          action: "write-note",
          label: "School lesson note",
          group: "teaching",
          priority: 25,
          href,
          enabled: true,
          surface: "primary",
          emphasis: "primary",
        });
      }
    }
    for (const entry of identified) {
      actions.push({
        id: `write-note:${entry.bookingId}`,
        action: "write-note",
        label:
          context.entries.length === 1
            ? "Write up / notes"
            : `Write up ${entry.studentName ?? "student"}`,
        group: "teaching",
        priority: 30,
        href: `/bookings/${encodeURIComponent(entry.bookingId)}/lesson`,
        enabled: true,
        surface: "primary",
        emphasis: "secondary",
      });
    }
  }

  if (singleStudent !== null && context.canSetPracticeTodo) {
    actions.push({
      id: "assign-practice",
      action: "assign-practice",
      label: "Assign practice",
      group: "teaching",
      priority: 40,
      enabled: true,
      surface: "quick",
      emphasis: "secondary",
    });
  }

  if (context.canViewStudent && singleStudent !== null && singleStudent.studentId !== null) {
    actions.push({
      id: "view-student",
      action: "view-student",
      label: "View student",
      group: "teaching",
      priority: 50,
      href: `/students/${encodeURIComponent(singleStudent.studentId)}`,
      enabled: true,
      surface: "quick",
      emphasis: "secondary",
    });
  }

  if (context.canMatchStudent && !isSchoolSession) {
    for (const entry of context.entries.filter((entry) => entry.studentId === null)) {
      actions.push({
        id: `attribute-booking:${entry.bookingId}`,
        action: "attribute-booking",
        bookingId: entry.bookingId,
        label: context.participantCount === 1
          ? "Match booking to student"
          : `Match ${entry.studentName ?? "unmatched booking"} to student`,
        group: "admin",
        priority: 30,
        enabled: true,
        surface: "quick",
        emphasis: "secondary",
      });
    }
  }

  // One block-sale entry. The label names the form it opens: tutors keep the
  // "Add 5 credits" they know, and managers see the full form's own heading.
  if (context.blockSale !== null) {
    actions.push({
      id: "add-credits",
      action: "add-credits",
      label: context.blockSale === "managed" ? "Add / sell lesson block" : "Add 5 credits",
      group: "admin",
      priority: 60,
      enabled: true,
      surface: "more",
      emphasis: "tertiary",
    });
  }

  if (context.noShowEligible && !context.noShow) {
    actions.push({
      id: "mark-no-show",
      action: "mark-no-show",
      label: "Mark no-show",
      group: "admin",
      priority: 70,
      enabled: true,
      surface: "status",
      emphasis: "tertiary",
    });
  }

  if (context.noShow && context.canMutate) {
    actions.push({
      id: "undo-no-show",
      action: "undo-no-show",
      label: "Undo no-show",
      group: "admin",
      priority: 75,
      enabled: true,
      surface: "status",
      emphasis: "tertiary",
    });
  }

  if (context.canReversePayment) {
    actions.push({
      id: "mark-unpaid",
      action: "mark-unpaid",
      label: "Mark as unpaid",
      group: "admin",
      priority: 80,
      enabled: true,
      surface: "status",
      emphasis: "tertiary",
    });
  }

  if (!context.cancelled && !context.noShow && context.canMutate) {
    actions.push({
      id: "cancel-lesson",
      action: "cancel-lesson",
      label: "Cancel",
      group: "booking",
      priority: 90,
      enabled: true,
      surface: "primary",
      emphasis: "danger",
    });
  }

  return actions.sort((left, right) => left.priority - right.priority || left.id.localeCompare(right.id));
}

/** Enabled actions by default; hosts must opt in to displaying disabled entries. */
export function diaryActionsForSurface(
  actions: readonly DiaryQuickAction[],
  surface: DiaryQuickActionSurface,
  { includeDisabled = false }: { includeDisabled?: boolean } = {},
): DiaryQuickAction[] {
  return actions.filter((action) => action.surface === surface && (action.enabled || includeDisabled));
}
